(function installArtboxFamilyCatalog() {
  if (window.ArtboxFamilyCatalog) return;

  const sessions = new WeakMap();

  function mark(name) {
    try {
      window.performance?.mark?.(name);
    } catch (_error) {
      return;
    }
  }

  function readLinkedProductSignature(card) {
    try {
      const products = JSON.parse(card.querySelector('[data-artbox-linked-products]')?.textContent || '[]');
      return products
        .filter((product) => product?.id && product?.url)
        .map((product) => String(product.id))
        .sort()
        .join('|');
    } catch (_error) {
      return '';
    }
  }

  function cardFamilyKey(card) {
    if (card.dataset.artboxFamilyKey) return card.dataset.artboxFamilyKey;
    if (card.dataset.artboxSelectedProduct) return `product:${card.dataset.artboxSelectedProduct}`;
    const linkedSignature = readLinkedProductSignature(card);
    return linkedSignature ? `linked:${linkedSignature}` : '';
  }

  function serialiseCards(cards, sourcePage) {
    return Array.from(cards).map((card, sourceIndex) => ({
      familyKey: cardFamilyKey(card),
      price: Number(card.dataset.artboxSortPrice || 0),
      sourcePage,
      sourceIndex,
      node: card.ownerDocument === document ? card : null,
      html: card.ownerDocument === document ? '' : card.outerHTML,
    })).filter((record) => record.familyKey);
  }

  function mergeAvailablePages(session) {
    let committedThrough = 0;
    while (session.pages.has(committedThrough + 1)) committedThrough += 1;
    session.committedThrough = committedThrough;

    const winners = new Map();
    for (let pageNumber = 1; pageNumber <= committedThrough; pageNumber += 1) {
      const records = [...session.pages.get(pageNumber)].sort((left, right) => left.sourceIndex - right.sourceIndex);
      records.forEach((record) => {
        if (!winners.has(record.familyKey)) winners.set(record.familyKey, record);
      });
    }
    session.records = Array.from(winners.values()).sort((left, right) => {
      const priceOrder = session.priceSort === 'price-ascending' ? left.price - right.price
        : session.priceSort === 'price-descending' ? right.price - left.price : 0;
      return priceOrder || left.sourcePage - right.sourcePage || left.sourceIndex - right.sourceIndex;
    });
  }

  function recordNode(record) {
    if (record.node) return record.node;
    const template = document.createElement('template');
    template.innerHTML = record.html.trim();
    record.node = template.content.firstElementChild;
    return record.node;
  }

  function nodesFor(records) {
    return records.map(recordNode).filter(Boolean);
  }

  function canLoadAnotherBatch(root, session) {
    const pageSize = Number(root.dataset.artboxFamilyPageSize || 20);
    const buffered = session.records.length - session.visible;
    if (session.loading || root.dataset.artboxFamilyError === 'true'
      || (session.priceSort && session.missingPages.size > 0)) return false;
    return buffered >= pageSize
      || (session.exactTotal !== null && buffered > 0)
      || session.missingPages.size > 0;
  }

  function plainTemplate(template) {
    return new DOMParser().parseFromString(template, 'text/html').body.textContent || template;
  }

  function writeVisibleCount(root, session) {
    const viewed = root.querySelector('[data-artbox-family-viewed]');
    if (!viewed) return;
    const template = plainTemplate(viewed.dataset.shownTemplate || '__CURRENT__ products shown');
    viewed.textContent = template.replace('__CURRENT__', String(session.visible));
    const resultCount = root.querySelector('[data-result-count]');
    if (resultCount?.dataset.shownTemplate) {
      resultCount.textContent = plainTemplate(resultCount.dataset.shownTemplate).replace('__CURRENT__', String(session.visible));
    }
  }

  function writeExactCount(root, session) {
    const viewed = root.querySelector('[data-artbox-family-viewed]');
    if (viewed) {
      const template = viewed.dataset.template || '__CURRENT__ / __TOTAL__';
      viewed.textContent = plainTemplate(template)
        .replace('__CURRENT__', String(session.visible))
        .replace('__TOTAL__', String(session.exactTotal));
    }
    const progress = root.querySelector('[data-artbox-family-progress]');
    if (progress) {
      const percentage = session.exactTotal ? Math.min(100, (session.visible / session.exactTotal) * 100) : 0;
      progress.style.width = `${percentage}%`;
    }
    const resultCount = root.querySelector('[data-result-count]');
    if (resultCount) resultCount.textContent = `${session.exactTotal} ${resultCount.dataset.label || ''}`.trim();
  }

  function setFooterState(root, session, state) {
    const pagination = root.querySelector('[data-artbox-family-pagination]');
    if (!pagination) return;
    pagination.hidden = false;
    pagination.dataset.artboxFamilyState = state;

    const status = pagination.querySelector('[data-artbox-family-status]');
    if (status) status.hidden = state !== 'resolving';
    const retry = pagination.querySelector('[data-artbox-family-retry]');
    if (retry) retry.hidden = session.retrying || root.dataset.artboxFamilyError !== 'true';
    const viewed = pagination.querySelector('[data-artbox-family-viewed]');
    const progress = pagination.querySelector('[data-artbox-family-progress]');
    if (state === 'resolving' || state === 'ready-more' || state === 'error') {
      writeVisibleCount(root, session);
      progress?.style.removeProperty('width');
    } else {
      writeExactCount(root, session);
    }

    const loadMore = pagination.querySelector('[data-artbox-family-load-more]');
    if (loadMore) {
      const enabled = canLoadAnotherBatch(root, session);
      loadMore.disabled = !enabled;
      loadMore.setAttribute('aria-disabled', String(!enabled));
      loadMore.hidden = state === 'complete';
    }
  }

  function refreshFooter(root, session) {
    if (session.exactTotal === null) {
      const state = root.dataset.artboxFamilyError === 'true'
        ? 'error'
        : session.loading || root.dataset.artboxFamilyReady !== 'true'
          ? 'resolving'
          : 'ready-more';
      setFooterState(root, session, state);
      return;
    }
    setFooterState(root, session, session.visible >= session.exactTotal ? 'complete' : 'exact');
  }

  function renderFirstBatch(root, session) {
    if (session.priceSort && session.missingPages.size > 0) {
      root.querySelector('.artbox-collection__grid')?.style.setProperty('visibility', 'hidden');
      refreshFooter(root, session);
      return;
    }
    if (root.dataset.artboxFamilyReady === 'true') {
      refreshFooter(root, session);
      return;
    }
    const grid = root.querySelector('.artbox-collection__grid');
    if (!grid) return;
    const pageSize = Number(root.dataset.artboxFamilyPageSize || 20);
    const records = session.records.slice(0, pageSize);
    if (session.priceSort) grid.style.removeProperty('visibility');
    const nodes = nodesFor(records);
    const retained = new Set(nodes);
    Array.from(grid.children).forEach((node) => {
      if (!retained.has(node)) node.remove();
    });
    let next = grid.firstElementChild;
    nodes.forEach((node) => {
      if (node === next) next = next.nextElementSibling;
      else grid.insertBefore(node, next);
    });
    session.visible = records.length;

    if (records.length >= pageSize || session.missingPages.size === 0) {
      root.dataset.artboxFamilyReady = 'true';
      grid.dataset.artboxFamilyHydrated = 'true';
      if (!session.firstBatchMarked) {
        session.firstBatchMarked = true;
        mark('artbox-family-first-batch');
      }
    }
    refreshFooter(root, session);
  }

  function appendNextFamilyBatch(root, session) {
    if (!canLoadAnotherBatch(root, session)) return;
    const grid = root.querySelector('.artbox-collection__grid');
    if (!grid) return;
    const pageSize = Number(root.dataset.artboxFamilyPageSize || 20);
    const records = session.records.slice(session.visible, session.visible + pageSize);
    grid.append(...nodesFor(records));
    session.visible += records.length;
    refreshFooter(root, session);
  }

  function continuationUrl(root, pageNumber) {
    const url = new URL(window.location.href);
    url.searchParams.set('view', root.dataset.artboxFamilyResponseView);
    url.searchParams.set('page', String(pageNumber));
    return url;
  }

  async function fetchFamilyPage(root, session, pageNumber) {
    const generation = session.generation;
    const signal = session.controller.signal;
    const response = await fetch(continuationUrl(root, pageNumber), { signal });
    if (sessions.get(root)?.generation !== generation || signal.aborted) return false;
    if (!response.ok) throw new Error(`Family catalogue page failed: ${response.status}`);
    const responseText = await response.text();
    if (sessions.get(root)?.generation !== generation || signal.aborted) return false;
    const documentFragment = new DOMParser().parseFromString(responseText, 'text/html');
    const responseRoot = documentFragment.querySelector('[data-artbox-family-response]');
    if (!responseRoot) throw new Error('Family catalogue response is missing its root');
    const cards = responseRoot.querySelectorAll(':scope > [data-artbox-linked-card]');
    session.pages.set(pageNumber, serialiseCards(cards, pageNumber));
    session.missingPages.delete(pageNumber);
    session.failedPages.delete(pageNumber);
    mergeAvailablePages(session);
    return true;
  }

  function markPageFailure(root, session, pageNumber) {
    session.failedPages.add(pageNumber);
    session.retrying = false;
    root.dataset.artboxFamilyError = 'true';
    refreshFooter(root, session);
  }

  function nextRequiredPage(session) {
    return Array.from(session.missingPages)
      .filter((pageNumber) => !session.failedPages.has(pageNumber))
      .sort((left, right) => left - right)[0] || null;
  }

  async function fetchUntil(root, session, targetCount) {
    while ((session.records.length < targetCount || session.priceSort) && session.missingPages.size > 0 && !session.controller.signal.aborted) {
      const pageNumber = nextRequiredPage(session);
      if (!pageNumber) break;
      try {
        await fetchFamilyPage(root, session, pageNumber);
      } catch (error) {
        if (error.name === 'AbortError') throw error;
        markPageFailure(root, session, pageNumber);
        break;
      }
    }
  }

  function finishExactTotal(root, session) {
    if (session.missingPages.size > 0 || session.controller.signal.aborted) return;
    mergeAvailablePages(session);
    session.exactTotal = session.records.length;
    session.retrying = false;
    root.dataset.artboxFamilyTotalReady = 'true';
    delete root.dataset.artboxFamilyError;
    if (!session.exactTotalMarked) {
      session.exactTotalMarked = true;
      mark('artbox-family-exact-total');
    }
    renderFirstBatch(root, session);
    refreshFooter(root, session);
  }

  async function hydrateCurrentCatalogue(root, session) {
    const grid = root.querySelector('.artbox-collection__grid');
    if (!grid || session.loading) return;
    const pageSize = Number(root.dataset.artboxFamilyPageSize || 20);
    session.loading = true;
    try {
      mergeAvailablePages(session);
      renderFirstBatch(root, session);
      await fetchUntil(root, session, pageSize);
      if (session.missingPages.size === 0) finishExactTotal(root, session);
      renderFirstBatch(root, session);
    } finally {
      session.loading = false;
      refreshFooter(root, session);
    }
  }

  async function loadNextFamilyBatch(root, session) {
    if (!session || session.loading || root.dataset.artboxFamilyError === 'true') return session;
    const pageSize = Number(root.dataset.artboxFamilyPageSize || 20);
    session.pendingAppend = true;
    session.loading = true;
    refreshFooter(root, session);
    try {
      await fetchUntil(root, session, session.visible + pageSize);
      if (session.missingPages.size === 0) finishExactTotal(root, session);
    } finally {
      session.loading = false;
    }
    appendNextFamilyBatch(root, session);
    refreshFooter(root, session);
    return session;
  }

  async function retryMissing(root) {
    const session = sessions.get(root);
    if (!session || session.missingPages.size === 0) return session || null;
    if (session.loading) await session.loadPromise;
    if (sessions.get(root)?.generation !== session.generation || session.missingPages.size === 0) return session;

    session.controller.abort();
    session.controller = new AbortController();
    session.failedPages.clear();
    session.retrying = true;
    delete root.dataset.artboxFamilyError;
    root.querySelector('[data-artbox-family-retry]')?.setAttribute('hidden', '');
    session.loading = true;
    refreshFooter(root, session);
    try {
      const pageSize = Number(root.dataset.artboxFamilyPageSize || 20);
      const targetCount = session.pendingAppend ? session.visible + pageSize : pageSize;
      await fetchUntil(root, session, targetCount);
      if (session.missingPages.size === 0) finishExactTotal(root, session);
    } finally {
      session.loading = false;
      if (session.missingPages.size > 0) {
        if (session.failedPages.size > 0) root.dataset.artboxFamilyError = 'true';
      }
    }
    session.retrying = false;
    if (session.pendingAppend) appendNextFamilyBatch(root, session);
    else renderFirstBatch(root, session);
    refreshFooter(root, session);
    return session;
  }

  function initialise(root) {
    if (root.dataset.artboxFamilyCatalog !== 'true') return null;
    const current = sessions.get(root);
    if (current) return current;

    const grid = root.querySelector('.artbox-collection__grid');
    if (!grid) return null;
    const initialCards = grid.querySelectorAll(':scope > [data-artbox-linked-card]');
    const sourcePages = Number(root.dataset.artboxFamilyPages || 1);
    const totalPages = Number.isFinite(sourcePages) ? Math.max(1, Math.floor(sourcePages)) : 1;
    const sourcePage = Number(root.dataset.artboxFamilyPage || 1);
    const initialPage = Number.isFinite(sourcePage) ? Math.min(totalPages, Math.max(1, Math.floor(sourcePage))) : 1;
    let defaultSort = '';
    try { defaultSort = JSON.parse(root.querySelector('[data-ntajax-options]')?.getAttribute('data-ntajax-options') || '{}').sort_by || ''; }
    catch (_error) { /* Keep native source order when sorting metadata is malformed. */ }
    const sortBy = new URL(window.location.href).searchParams.get('sort_by') || defaultSort;
    const session = {
      priceSort: ['price-ascending', 'price-descending'].includes(sortBy) ? sortBy : '',
      controller: new AbortController(),
      pages: new Map([[initialPage, serialiseCards(initialCards, initialPage)]]),
      missingPages: new Set(Array.from({ length: totalPages }, (_, index) => index + 1).filter(pageNumber => pageNumber !== initialPage)),
      records: [],
      visible: 0,
      exactTotal: null,
      generation: Symbol('family-session'),
      loading: false,
      committedThrough: 0,
      failedPages: new Set(),
      firstBatchMarked: false,
      exactTotalMarked: false,
      retrying: false,
      pendingAppend: false,
    };
    session.handleClick = (event) => {
      const loadMore = event.target.closest?.('[data-artbox-family-load-more]');
      if (loadMore && root.contains(loadMore)) {
        session.loadPromise = loadNextFamilyBatch(root, session);
        return;
      }
      const retry = event.target.closest?.('[data-artbox-family-retry]');
      if (retry && root.contains(retry)) retryMissing(root);
    };
    root.addEventListener('click', session.handleClick);
    sessions.set(root, session);
    mark('artbox-family-start');
    session.loadPromise = hydrateCurrentCatalogue(root, session).catch((error) => {
      if (error.name !== 'AbortError') throw error;
    });
    return session;
  }

  function destroy(root) {
    const session = sessions.get(root);
    if (session) {
      sessions.delete(root);
      session.controller.abort();
      root.removeEventListener('click', session.handleClick);
    }
    delete root.dataset.artboxFamilyReady;
    delete root.dataset.artboxFamilyTotalReady;
    delete root.dataset.artboxFamilyError;
  }

  window.ArtboxFamilyCatalog = { initialise, destroy, retryMissing };
})();
